import React from "react";
import { styled } from "@mui/material/styles";
import _ from "lodash";
import { Grid, Chip, Autocomplete, TextField } from "@mui/material";
import { useQuery, useMutation } from "@apollo/client";
import Loader from "ui/components/Loader";
import CurrentUserAllCategoriesQuery from "ui/queries/CurrentUserAllCategoriesQuery.graphql";
import CurrentSalespersonParcelCategoriesQuery from "ui/sites/acquisitions/queries/CurrentSalespersonParcelCategoriesQuery.graphql";
import CurrentUnderwriterParcelCategoriesQuery from "ui/sites/underwriting/queries/CurrentUnderwriterParcelCategoriesQuery.graphql";
import ParcelAddCategoryMutation from "ui/queries/ParcelAddCategoryMutation.graphql";
import ParcelRemoveCategoryMutation from "ui/queries/ParcelRemoveCategoryMutation.graphql";

const PREFIX = "ParcelCategories";

const classes = {
  categoryChip: `${PREFIX}-categoryChip`,
  autocompleteInput: `${PREFIX}-autocompleteInput`,
  autocompleteWrapper: `${PREFIX}-autocompleteWrapper`
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.categoryChip}`]: {
    marginRight: theme.spacing(1)
  },

  [`& .${classes.autocompleteInput}`]: {
    backgroundColor: "white",
    margin: theme.spacing(1),
    borderRadius: "5px"
  },

  [`& .${classes.autocompleteWrapper}`]: {
    minWidth: 300,
    paddingRight: theme.spacing(2)
  }
}));

const typeToQueries = {
  acquisitions: CurrentSalespersonParcelCategoriesQuery,
  underwriting: CurrentUnderwriterParcelCategoriesQuery
};

const typeToSlug = {
  acquisitions: "currentSalesperson",
  underwriting: "currentUnderwriter"
};

function ParcelCategories({ parcelId, type = "acquisitions" }) {
  const { data: allCategoriesData, loading: allCategoriesLoading } = useQuery(
    CurrentUserAllCategoriesQuery,
    { variables: { first: 5000 } }
  );
  const {
    data: parcelCategoryData,
    loading: categoriesLoading,
    refetch
  } = useQuery(typeToQueries[type], { variables: { parcelId } });
  const [addCategory, { loading: addCategoryLoading }] = useMutation(
    ParcelAddCategoryMutation,
    { refetchQueries: [refetch] }
  );
  const [removeCategory, { loading: removeCategoryLoading }] = useMutation(
    ParcelRemoveCategoryMutation,
    { refetchQueries: [refetch] }
  );

  if (
    allCategoriesLoading ||
    categoriesLoading ||
    addCategoryLoading ||
    removeCategoryLoading
  ) {
    return (
      <StyledGrid
        container
        size={12}
        sx={{
          justifyContent: "flex-end"
        }}
      >
        <Loader />
      </StyledGrid>
    );
  }

  const categoryNodes = _.map(
    _.get(allCategoriesData, "currentUser.allCategories.edges", []),
    "node"
  );
  const categoryToId = _.fromPairs(
    _.map(categoryNodes, ({ id, name }) => [name, id])
  );

  const alphabeticalCategories = _.map(
    _.sortBy(categoryNodes, ({ name }) => _.lowerCase(name)),
    ({ name }) => ({
      label: name
    })
  );

  const parcelCategories = _.get(
    parcelCategoryData,
    `${typeToSlug[type]}.parcel.categories.edges`
  );

  return (
    <Grid
      container
      size={12}
      sx={{
        justifyContent: "flex-end"
      }}
    >
      {_.map(parcelCategories, ({ node }) => (
        <Grid
          key={`${parcelId}-${node.id}`}
          className={classes.categoryChip}
          sx={{ alignContent: "center" }}
        >
          <Chip
            label={node.name}
            onDelete={() =>
              removeCategory({ variables: { parcelId, categoryId: node.id } })
            }
            style={{ marginRight: "5px" }}
          />
        </Grid>
      ))}
      <Grid className={classes.autocompleteWrapper}>
        <Autocomplete
          sx={{ marginRight: "10px" }}
          size="small"
          disablePortal
          options={alphabeticalCategories}
          onChange={(event, { label }) => {
            if (categoryToId[label]) {
              console.log("here now");
              addCategory({
                variables: { parcelId, categoryId: categoryToId[label] }
              });
            }
          }}
          isOptionEqualToValue={(option, { label }) => option.label === label}
          renderInput={params => (
            <TextField
              style={{ minWidth: "200px" }}
              disabled={addCategoryLoading}
              {...params}
              label="Categories"
              className={classes.autocompleteInput}
            />
          )}
        />
      </Grid>
    </Grid>
  );
}

export default ParcelCategories;
